import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Clock, LogIn, Home, Receipt } from 'lucide-react';

const REASON_COPY = {
  auth: {
    title: 'Please sign in again',
    body: 'Your session has ended. Sign back in to continue with your payment or view your orders.',
  },
  finished: {
    title: 'Payment session already finished',
    body: 'This payment has already been completed. You can check the status from your orders.',
  },
  cancelled: {
    title: 'Payment was cancelled',
    body: 'This payment session was cancelled. If you still want to pay, start again from your orders.',
  },
  expired: {
    title: 'Payment link has expired',
    body: 'This payment link is no longer valid. You can create a fresh one from your orders.',
  },
  unavailable: {
    title: 'Payment page unavailable',
    body: "We couldn't load this payment session. Please head back and try again.",
  },
};

export default function SessionExpiredPage() {
  const [params] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const reason = params.get('reason') || 'finished';
  const copy = REASON_COPY[reason] || REASON_COPY.finished;

  return (
    <div className="min-h-screen bg-[#FCFAF8]" data-testid="session-expired-page">
      <Header />
      <main className="max-w-lg mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50 text-center">
          <div className="w-16 h-16 rounded-full bg-[#FFF5F5] flex items-center justify-center mx-auto mb-5">
            <Clock size={28} className="text-[#6B1F2B]" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] mb-3" data-testid="session-expired-title">
            {copy.title}
          </h1>
          <p className="font-body text-[#78716C] mb-8 leading-relaxed">
            {copy.body}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {reason === 'auth' && !isAuthenticated ? (
              <Link
                to="/"
                className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md inline-flex items-center justify-center gap-2"
                data-testid="session-expired-signin-btn"
              >
                <LogIn size={16} /> Sign in
              </Link>
            ) : (
              <Link
                to="/orders"
                className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md inline-flex items-center justify-center gap-2"
                data-testid="session-expired-orders-btn"
              >
                <Receipt size={16} /> View my orders
              </Link>
            )}
            <Link
              to="/"
              className="border border-cream-100 text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold transition-all hover:bg-cream-50 inline-flex items-center justify-center gap-2"
              data-testid="session-expired-home-btn"
            >
              <Home size={16} /> Go to homepage
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
